import { useCallback, useEffect, useRef, useState } from "react";
import useApi from "./useApi";

export const PAGE_SIZE = 20;

/**
 * Reads the grand total off the response. Returns null when the endpoint
 * doesn't report one.
 *
 * Deliberately excludes `count` (what /user/album sends): in that response
 * `count` equals the number of records in the page, so it's ambiguous - it may
 * be a per-page count rather than a total, and trusting it would stop paging
 * after the first full page. A short page already signals the end, which is
 * what actually ends the album list today.
 */
const readTotal = (result) => {
  const raw =
    result?.total ?? result?.totalCount ?? result?.totalRecords ?? null;
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const appendPaging = (endpoint, limit, offset) => {
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}limit=${limit}&offset=${offset}`;
};

/**
 * Paginated list loader with an IntersectionObserver sentinel.
 *
 * Attach `sentinelRef` to an element rendered *after* the list — when it scrolls
 * into view the next `pageSize` records are requested at the current offset.
 *
 * @param {string}   endpoint    path, without the limit/offset query string
 * @param {string}   method      http method the endpoint expects
 * @param {number}   pageSize    records per request
 * @param {Function} selectItems pulls the array out of the response body
 * @param {Function} getKey      stable identity for an item, used to de-duplicate
 * @param {boolean}  enabled     set false to hold off the first request
 */
export default function useInfiniteList({
  endpoint,
  method = "post",
  pageSize = PAGE_SIZE,
  selectItems = (result) => result?.data,
  getKey = (item, index) => item?._id ?? item?.id ?? index,
  enabled = true,
}) {
  const { trigger } = useApi();

  const [items, setItems] = useState([]);
  // Starts true so the first paint shows the spinner, not the empty state.
  const [loading, setLoading] = useState(enabled);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef(null);

  // `trigger` is a fresh closure on every render, so keep it in a ref and out of
  // the effect/callback dependency lists.
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  const offsetRef = useRef(0);
  const inFlightRef = useRef(false);
  const seenKeysRef = useRef(new Set());
  const hasMoreRef = useRef(true);

  const load = useCallback(async () => {
    if (inFlightRef.current || !hasMoreRef.current) return;
    inFlightRef.current = true;

    const offset = offsetRef.current;
    const isFirstPage = offset === 0;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);

    try {
      const result = await triggerRef.current({
        endpoint: appendPaging(endpoint, pageSize, offset),
        method,
        requireAuth: false,
      });

      if (String(result?.sts) !== "0") {
        setError(result?.msg || "Unable to load the list right now.");
        return;
      }

      const page = selectItems(result);
      const pageItems = Array.isArray(page) ? page : [];

      // Drop records already held. Guards both overlapping pages and an endpoint
      // that ignores limit/offset and replays the full list on every request.
      const fresh = pageItems.filter((item, index) => {
        const key = String(getKey(item, offset + index));
        if (seenKeysRef.current.has(key)) return false;
        seenKeysRef.current.add(key);
        return true;
      });

      if (fresh.length > 0) {
        setItems((prev) => [...prev, ...fresh]);
      }

      offsetRef.current = offset + pageItems.length;

      const total = readTotal(result);

      // When the endpoint reports a total, that is the authority: it may serve a
      // smaller page than we asked for (the news endpoint caps at pageSize 10),
      // and a short page must not be mistaken for the end of the list.
      const exhausted =
        pageItems.length === 0 ||
        fresh.length === 0 ||
        (total !== null
          ? offsetRef.current >= total
          : pageItems.length < pageSize);

      if (exhausted) {
        hasMoreRef.current = false;
        setHasMore(false);
      }
      setError(null);
    } catch (err) {
      setError(err?.message || "Unable to load the list right now.");
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [endpoint, method, pageSize, selectItems, getKey]);

  const reload = useCallback(() => {
    offsetRef.current = 0;
    seenKeysRef.current = new Set();
    hasMoreRef.current = true;
    setItems([]);
    setHasMore(true);
    setError(null);
    load();
  }, [load]);

  // First page. The ref guard keeps StrictMode's double-mount from firing twice.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;
    load();
  }, [enabled, load]);

  // Request the next page once the sentinel comes into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || !enabled) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) load();
      },
      { rootMargin: "300px" } // start fetching before the user hits the bottom
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, enabled, load, items.length]);

  return {
    items,
    loading,
    loadingMore,
    error,
    hasMore,
    sentinelRef,
    loadMore: load,
    reload,
  };
}
